// src/components/HermodsPath/ImagePromptModal.js
// Modal for generating AI image prompts for Canva Magic Media or Leonardo AI

import React, { useState, useEffect, useCallback } from 'react';
import { X, Copy, Check, ExternalLink, Loader2, Wand2 } from 'lucide-react';
import { buildBasePrompt, enhancePrompt } from '../../services/imagePromptGenerator';

const TOOLS = [
  {
    id: 'canva',
    label: 'Canva Magic Media',
    description: 'Short, direct prompts',
    url: 'https://www.canva.com/magic-media/',
    icon: '\uD83C\uDFA8',
  },
  {
    id: 'leonardo',
    label: 'Leonardo AI',
    description: 'Detailed, stylistic prompts',
    url: 'https://app.leonardo.ai/ai-generations',
    icon: '\uD83D\uDDBC\uFE0F',
  },
];

const STORAGE_KEY = 'hermodsPath_preferredImageTool';

export default function ImagePromptModal({ isOpen, onClose, imageNote, platformId }) {
  const [selectedTool, setSelectedTool] = useState(
    () => localStorage.getItem(STORAGE_KEY) || 'canva'
  );
  const [prompt, setPrompt] = useState('');
  const [enhancing, setEnhancing] = useState(false);
  const [enhanced, setEnhanced] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  // Generate base prompt when modal opens or tool changes
  useEffect(() => {
    if (isOpen && imageNote && platformId) {
      setPrompt(buildBasePrompt(imageNote, selectedTool, platformId));
      setEnhanced(false);
      setError(null);
    }
  }, [isOpen, imageNote, platformId, selectedTool]);

  // Save tool preference
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, selectedTool);
  }, [selectedTool]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  const handleEnhance = useCallback(async () => {
    setEnhancing(true);
    setError(null);
    try {
      const result = await enhancePrompt(imageNote, selectedTool, platformId);
      setPrompt(result);
      setEnhanced(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setEnhancing(false);
    }
  }, [imageNote, selectedTool, platformId]);

  const handleCopy = useCallback(async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(prompt);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = prompt;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch {
      // Silently fail — user can manually select and copy
    }
  }, [prompt]);

  if (!isOpen) return null;

  const activeTool = TOOLS.find(t => t.id === selectedTool);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 w-full max-w-lg mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-700 flex items-center justify-between">
          <h3 className="text-white font-bold text-sm">Generate Image Prompt</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition">
            <X size={18} />
          </button>
        </div>

        {/* Tool selector */}
        <div className="px-5 pt-4 flex gap-2">
          {TOOLS.map(tool => (
            <button
              key={tool.id}
              onClick={() => setSelectedTool(tool.id)}
              className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-medium transition border-2 text-left ${
                selectedTool === tool.id
                  ? 'border-rose-500 bg-rose-500/10 text-rose-300'
                  : 'border-slate-600 bg-slate-700/50 text-slate-400 hover:border-slate-500'
              }`}
            >
              <span className="text-base mr-1.5">{tool.icon}</span>
              <span className="font-semibold">{tool.label}</span>
              <p className="text-[10px] mt-0.5 opacity-70">{tool.description}</p>
            </button>
          ))}
        </div>

        {/* Image note context */}
        <div className="px-5 pt-3">
          <p className="text-xs text-slate-500 mb-1">The Seer suggested:</p>
          <p className="text-xs text-slate-300 bg-slate-700/50 rounded-lg p-2 italic">{imageNote}</p>
        </div>

        {/* Generated prompt */}
        <div className="px-5 pt-3">
          <div className="flex items-center justify-between mb-1">
            <p className="text-xs text-slate-500">
              {enhanced ? 'AI-enhanced prompt' : 'Base prompt'} for {activeTool?.label}
            </p>
            {!enhanced && (
              <button
                onClick={handleEnhance}
                disabled={enhancing}
                className="text-xs text-rose-400 hover:text-rose-300 flex items-center gap-1 transition disabled:opacity-50"
              >
                {enhancing ? (
                  <><Loader2 size={10} className="animate-spin" /> Enhancing...</>
                ) : (
                  <><Wand2 size={10} /> Enhance with AI</>
                )}
              </button>
            )}
          </div>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            className="w-full bg-slate-900 text-slate-200 text-sm rounded-lg p-3 border border-slate-600 focus:ring-2 focus:ring-rose-500 focus:border-transparent resize-none leading-relaxed"
            rows={6}
          />
          {error && <p className="text-xs text-red-400 mt-1">{error}</p>}
        </div>

        {/* Actions */}
        <div className="px-5 py-4 flex gap-2">
          <button
            onClick={handleCopy}
            className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-1.5 ${
              copied
                ? 'bg-green-600 text-white'
                : 'bg-slate-700 text-slate-200 hover:bg-slate-600'
            }`}
          >
            {copied ? <><Check size={14} /> Copied!</> : <><Copy size={14} /> Copy Prompt</>}
          </button>
          <a
            href={activeTool?.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 py-2.5 rounded-lg text-sm font-medium transition flex items-center justify-center gap-1.5 bg-gradient-to-r from-rose-600 to-pink-600 text-white hover:from-rose-500 hover:to-pink-500"
          >
            <ExternalLink size={14} /> Open {activeTool?.label}
          </a>
        </div>
      </div>
    </div>
  );
}
